import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Button,
    Autocomplete,
    Popper,
    DialogActions,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/SaveAsRounded';
import CancelIcon from '@mui/icons-material/Cancel';
import { validateEmail, validatePhoneNumber } from '../../../Utils/helpers';
import { isValidIDOrCompanyID } from '../../../Utils/validationHelpers';
import Constants from '../../../assests/Constants/constants';
import CustomDatePicker from '../../Widgets/CustomDates/CustomDatePicker/CustomDatePicker';
function EditAccountCardModal(props) {
    const {
        open,
        onClose,
        rowData,
        isNew,
        onSave,
        palCodesOptions,
        sourceDeductionOptions,
        direction,
        t,
        toast,
        deductionTypeOptions,
        exactOccupationOptions,   // still passed, kept unchanged
    } = props;

    const [editedData, setEditedData] = useState({ ...rowData });

    /* ---------- shared input styles ---------- */
    const inputPropsStyle = {
        disableUnderline: true,
        className:
            `custom-input-box-sizing-toggle ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
        sx: {
            height: '48px',
            padding: '0 10px',
            borderRadius: '8px',
            background: '#F5F5F6',
            border: '1px solid transparent',
            '&:focus-within': {
                border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
            },
        },
    };

    const inputStyle = {
        style: { textAlign: direction === 'ltr' ? 'left' : 'right' },
    };

    const handleChange = (field, value) => {
        setEditedData(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = () => {
        let isValid = true;

        if (!editedData.cardId || !editedData.name) {
            toast.error(t('NotAllRequiredFieldsAreFilled'));
            isValid = false;
        }
        if (editedData.email && !validateEmail(editedData.email)) {
            toast.error(t('InvalidEmailAddress'));
            isValid = false;
        }
        if (editedData.phoneNumber && !validatePhoneNumber(editedData.phoneNumber)) {
            toast.error(t('InvalidPhoneNumber'));
            isValid = false;
        }
        if (editedData.accountCaseId && !isValidIDOrCompanyID(editedData.accountCaseId)) {
            toast.error(t('InvalidCompanyOrIDNumber'));
            isValid = false;
        }

        if (isValid) {
            onSave(editedData, isNew);
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle dir={direction}>
                {isNew ? t('AddAccountCard') : t('EditAccountCard')}
            </DialogTitle>

            <DialogContent dividers>
                <div className={`form-container ${direction}`}>

                    {/* 1. cardId | palCode */}
                    <div className="form-row">
                        {/* cardId */}
                        <div className="edit-form-row-item">
                            <div className="edit-form-row-item-title">
                                {t('AccountCard ID')}<span className="required-asterisk">*</span>
                            </div>
                            <TextField
                                fullWidth
                                value={editedData.cardId || ''}
                                onChange={e => handleChange('cardId', e.target.value)}
                                type="number"
                                variant="standard"
                                InputProps={inputPropsStyle}
                                inputProps={inputStyle}
                            />
                        </div>
                        {/* palCode */}
                        <div className="edit-form-row-item">
                            <div className="edit-form-row-item-title">{t('PalCode')}</div>
                            <Autocomplete
                                options={palCodesOptions}
                                getOptionLabel={o => o.label}
                                value={palCodesOptions.find(o => o.value === editedData.palCode) || null}
                                onChange={(_, v) => handleChange('palCode', v ? v.value : null)}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        variant="standard"
                                        InputProps={{ ...params.InputProps, ...inputPropsStyle }}
                                        inputProps={{ ...params.inputProps, ...inputStyle }}
                                    />
                                )}
                                PopperComponent={props => (
                                    <Popper {...props} style={{ ...props.style, direction }} />
                                )}
                            />
                        </div>
                    </div>

                    {/* 2. name | SourceDeductionAssociation */}
                    <div className="form-row">
                        {/* name */}
                        <div className="edit-form-row-item">
                            <div className="edit-form-row-item-title">
                                {t('AccountCard Name')}<span className="required-asterisk">*</span>
                            </div>
                            <TextField
                                fullWidth
                                value={editedData.name || ''}
                                onChange={e => handleChange('name', e.target.value)}
                                variant="standard"
                                InputProps={inputPropsStyle}
                                inputProps={inputStyle}
                            />
                        </div>
                        {/* SourceDeductionAssociation */}
                        <div className="edit-form-row-item">
                            <div className="edit-form-row-item-title">{t('SourceDeductionAssociation')}</div>
                            <Autocomplete
                                options={sourceDeductionOptions}
                                getOptionLabel={o => o.label}
                                value={sourceDeductionOptions.find(o => o.value === editedData.sourceDeductionAssociation) || null}
                                onChange={(_, v) => handleChange('sourceDeductionAssociation', v ? v.value : null)}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        variant="standard"
                                        InputProps={{ ...params.InputProps, ...inputPropsStyle }}
                                        inputProps={{ ...params.inputProps, ...inputStyle }}
                                    />
                                )}
                                PopperComponent={props => (
                                    <Popper {...props} style={{ ...props.style, direction }} />
                                )}
                            />
                        </div>
                    </div>

                    {/* 3. CompanyId | DeductionType */}
                    <div className="form-row">
                        {/* CompanyId */}
                        <div className="edit-form-row-item">
                            <div className="edit-form-row-item-title">{t('CompanyId')}</div>
                            <TextField
                                fullWidth
                                value={editedData.accountCaseId || ''}
                                onChange={e => handleChange('accountCaseId', e.target.value)}
                                variant="standard"
                                InputProps={inputPropsStyle}
                                inputProps={inputStyle}
                            />
                        </div>
                        {/* DeductionType */}
                        <div className="edit-form-row-item">
                            <div className="edit-form-row-item-title">{t('DeductionType')}</div>
                            <Autocomplete
                                options={deductionTypeOptions}
                                getOptionLabel={o => `${o.value} - ${o.label}`}
                                value={deductionTypeOptions.find(o => o.value === editedData.deductionType) || null}
                                onChange={(_, v) => handleChange('deductionType', v ? v.value : null)}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        variant="standard"
                                        InputProps={{ ...params.InputProps, ...inputPropsStyle }}
                                        inputProps={{ ...params.inputProps, ...inputStyle }}
                                    />
                                )}
                                PopperComponent={props => (
                                    <Popper {...props} style={{ ...props.style, direction }} />
                                )}
                            />
                        </div>
                    </div>

                    {/* 4. phoneNumber | DeductionRate */}
                    <div className="form-row">
                        {/* phoneNumber */}
                        <div className="edit-form-row-item">
                            <div className="edit-form-row-item-title">{t('PhoneNumber')}</div>
                            <TextField
                                fullWidth
                                value={editedData.phoneNumber || ''}
                                onChange={e => handleChange('phoneNumber', e.target.value)}
                                type="number"
                                variant="standard"
                                InputProps={inputPropsStyle}
                                inputProps={inputStyle}
                            />
                        </div>
                        {/* DeductionRate */}
                        <div className="edit-form-row-item">
                            <div className="edit-form-row-item-title">{t('DeductionRate')}</div>
                            <TextField
                                fullWidth
                                value={editedData.deductionRate || ''}
                                onChange={e => handleChange('deductionRate', parseFloat(e.target.value))}
                                variant="standard"
                                InputProps={inputPropsStyle}
                                inputProps={{ ...inputStyle, type: 'number', step: '0.01' }}
                            />
                        </div>
                    </div>

                    {/* 5. Location | DeductionValidity */}
                    <div className="form-row">
                        {/* Location */}
                        <div className="edit-form-row-item">
                            <div className="edit-form-row-item-title">{t('Location')}</div>
                            <TextField
                                fullWidth
                                value={editedData.location || ''}
                                onChange={e => handleChange('location', e.target.value)}
                                variant="standard"
                                InputProps={inputPropsStyle}
                                inputProps={inputStyle}
                            />
                        </div>
                        {/* DeductionValidity */}
                        <div className="edit-form-row-item">
                            <div className="edit-form-row-item-title">{t('DeductionValidity')}</div>
                            <CustomDatePicker
                                date={editedData.deductionValidity ? editedData.deductionValidity : null}
                                onDateChange={d => handleChange('deductionValidity', d ? d.toISOString() : null)}
                                height="48px"
                            />
                        </div>
                    </div>

                    {/* 6. Email | TaxAssessor */}
                    <div className="form-row">
                        {/* Email */}
                        <div className="edit-form-row-item">
                            <div className="edit-form-row-item-title">{t('Email')}</div>
                            <TextField
                                fullWidth
                                value={editedData.email || ''}
                                onChange={e => handleChange('email', e.target.value)}
                                type="email"
                                variant="standard"
                                InputProps={inputPropsStyle}
                                inputProps={inputStyle}
                            />
                        </div>
                        {/* TaxAssessor */}
                        <div className="edit-form-row-item">
                            <div className="edit-form-row-item-title">{t('TaxAssessor')}</div>
                            <Autocomplete
                                options={Constants.TaxAssessorOptionsString}
                                getOptionLabel={o => `${o.value} - ${o.label}`}
                                value={Constants.TaxAssessorOptionsString.find(o => o.value === editedData.taxAssessor) || null}
                                onChange={(_, v) => handleChange('taxAssessor', v ? v.value : null)}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        variant="standard"
                                        InputProps={{ ...params.InputProps, ...inputPropsStyle }}
                                        inputProps={{ ...params.inputProps, ...inputStyle }}
                                    />
                                )}
                                PopperComponent={props => (
                                    <Popper {...props} style={{ ...props.style, direction }} />
                                )}
                            />
                        </div>
                    </div>

                    {/* 7. ExactOccupation | (empty) */}
                    <div className="form-row">
                        <div className="edit-form-row-item">
                            <div className="edit-form-row-item-title">{t('ExactOccupation')}</div>
                            <TextField
                                fullWidth
                                value={editedData.exactOccupation || ''}
                                onChange={e => handleChange('exactOccupation', e.target.value)}
                                variant="standard"
                                InputProps={inputPropsStyle}
                                inputProps={inputStyle}
                            />
                        </div>
                        {/* intentionally left blank for grid alignment */}
                        <div className="edit-form-row-item" />
                    </div>
                </div>
            </DialogContent>

            <DialogActions dir={direction}>
                <Button onClick={onClose} startIcon={<CancelIcon />}>{t('Cancel')}</Button>
                <Button onClick={handleSave} startIcon={<SaveIcon />} color="primary" data-testid="edit-account-card-save-button">{t('Save')}</Button>
            </DialogActions>
        </Dialog>
    );
}

export default EditAccountCardModal;
